'use client';

import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Sparkles, Flame, ShieldCheck, Heart, Award, ArrowRight } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

export default function AboutPage() {
  return (
    <main className="min-h-screen flex flex-col bg-white">
      <Header />

      {/* 1. Hero */}
      <section className="bg-[#0A261A] text-white py-16 sm:py-24 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4">
          <div className="inline-flex items-center space-x-2 px-3.5 py-1 rounded-full bg-white/10 border border-[#F5B52E]/40 text-[#F5B52E] text-xs font-bold tracking-wider uppercase">
            <Sparkles className="w-3.5 h-3.5" />
            <span>Generational Culinary Pride</span>
          </div>

          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-white leading-tight">
            THE ELAVARASI TRADITION
          </h1>

          <p className="text-sm sm:text-base text-neutral-300 max-w-2xl mx-auto font-medium leading-relaxed">
            Founded over four decades ago in the temple city of Madurai, Elavarasi Foods began with a singular promise: 
            to never compromise on the purity of ingredients. Real wood-fired kadai frying, pure cold-pressed oils, and recipes passed down three generations.
          </p>
        </div>
      </section>

      {/* 2. Pillars */}
      <section className="py-16 bg-white border-b border-neutral-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          
          {/* Pillar 1: Wood Fired */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-6 space-y-4">
              <span className="text-xs font-bold text-amber-700 uppercase tracking-wider flex items-center space-x-1.5">
                <Flame className="w-4 h-4 text-amber-600" />
                <span>Culinary Authenticity</span>
              </span>
              <h2 className="text-2xl sm:text-3xl font-black text-neutral-900 tracking-tight">
                The Forgotten Art of Wood-Fired Kadai Frying
              </h2>
              <p className="text-xs sm:text-sm text-neutral-600 leading-relaxed font-medium">
                Modern factories use automated gas flash fryers that blister chips on the outside while leaving moisture trapped inside. 
                At Elavarasi, our master cooks still use heavy, traditional iron vats heated over slow firewood flames. 
                The steady, radiant heat cooks raw plantains and tapioca uniformly to their core, creating that iconic, brittle snap that stays crisp for weeks.
              </p>
            </div>
            <div className="lg:col-span-6 relative h-72 sm:h-80 rounded-3xl overflow-hidden bg-neutral-100 shadow-md">
              <Image
                src="/images/hero_spread.jpg"
                alt="Wood-Fired Frying Spread"
                fill
                unoptimized
                className="object-cover"
              />
            </div>
          </div>

          {/* Pillar 2: Pure Oils */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-6 order-2 lg:order-1 relative h-72 sm:h-80 rounded-3xl overflow-hidden bg-neutral-100 shadow-md">
              <Image
                src="/images/weekend_crunch_banner.jpg"
                alt="Cold Pressed Oil Snacks"
                fill
                unoptimized
                className="object-cover"
              />
            </div>
            <div className="lg:col-span-6 order-1 lg:order-2 space-y-4">
              <span className="text-xs font-bold text-emerald-700 uppercase tracking-wider flex items-center space-x-1.5">
                <ShieldCheck className="w-4 h-4 text-emerald-600" />
                <span>Our Unbroken Promise</span>
              </span>
              <h2 className="text-2xl sm:text-3xl font-black text-neutral-900 tracking-tight">
                100% Pure Cold-Pressed Oils. Zero Palm Oil.
              </h2>
              <p className="text-xs sm:text-sm text-neutral-600 leading-relaxed font-medium">
                Over 90% of commercial packaged snacks in India use cheap, hydrogenated palm oil that coats your throat with greasy residue. 
                We exclusively use expeller-pressed, native groundnut oil from Tamil Nadu farms and fragrant cold-pressed coconut oil from Kerala. 
                Rich in natural antioxidants, delightfully light on the stomach, and free of trans fats.
              </p>
            </div>
          </div>

        </div>
      </section>

      {/* 3. CTA */}
      <section className="bg-[#FAFBF9] py-14 text-center">
        <div className="max-w-2xl mx-auto px-4 space-y-4">
          <h3 className="text-2xl sm:text-3xl font-black text-neutral-900 uppercase tracking-tight">
            Ready to Taste Real South Indian Crunch?
          </h3>
          <p className="text-xs sm:text-sm text-neutral-500 font-medium">
            Discover our complete assortment of chips, Kai Murukku, and traditional sweets freshly prepared for your home.
          </p>
          <div className="pt-2">
            <Link
              href="/shop"
              className="inline-flex items-center space-x-2 px-8 py-3.5 rounded-full bg-emerald-800 hover:bg-emerald-900 text-white font-bold text-xs uppercase tracking-wider shadow-md transition-all"
            >
              <span>Explore The Catalogue</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
